import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {object, SchemaOf} from 'yup';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useCancelRentalOrderVehicleMutation} from '@dms/api/rental';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId, yupString} from 'shared/web';

import {useCodeList} from '../../hooks/useCodeList';
import {handleApiError} from '../../utils/handleApiError';

type RentalOrderCancelForm = {
  cancellationReasonId: string;
};

interface RentalOrderCancelDialogProps extends RequiredTestIdProps {
  rentalOrderId: string;
}

export function RentalOrderCancelDialog(props: RentalOrderCancelDialogProps) {
  const [codes, {isLoading: isCodeListLoading, isError}] = useCodeList('cancellationReason');
  const [cancelRentalOrder, {isLoading: isCancellingRentalOrder}] =
    useCancelRentalOrderVehicleMutation();

  const onSubmit: FormSubmitHandler<RentalOrderCancelForm> = async (data) => {
    await cancelRentalOrder({
      rentalOrderId: props.rentalOrderId,
      cancellationReasonId: data.cancellationReasonId,
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('general.notifications.successfullyCancelled')))
      .then(() => closeCurrentDialog())
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isCodeListLoading} isError={isError} minHeight={25}>
      <Form<RentalOrderCancelForm> schema={schema} onSubmit={onSubmit}>
        {(control, formApi) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              type="choice"
              name="cancellationReasonId"
              label={i18n.t('entity.rental.labels.cancellationReason')}
              isRequired
              options={
                codes?.map((code) => ({
                  label: code.name,
                  value: code.codeId,
                })) ?? []
              }
              data-testid={suffixTestId('cancellationReasonId', props)}
            />
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                onClick={closeCurrentDialog}
                isDisabled={isCancellingRentalOrder}
                title={i18n.t('general.actions.discard')}
                data-testid={suffixTestId('cancel', props)}
              />
              <FormButton
                variant="primary"
                type="submit"
                control={control}
                isLoading={isCancellingRentalOrder}
                isDisabled={
                  isNilOrEmpty(codes) || isNilOrEmpty(formApi.watch('cancellationReasonId'))
                }
                title={i18n.t('page.rentalOrder.labels.cancelOrder')}
                data-testid={suffixTestId('confirm', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

const schema: SchemaOf<RentalOrderCancelForm> = object({
  cancellationReasonId: yupString.required(),
});
